/**
 * 模态管理器负责当多个 Modal 同时存在时，保证其正常工作
 *
 * 针对嵌套 Modal，根据他们的嵌套关系，管理
 *  a 层级 zIndex
 *  b 锁定 lock
 *
 **/
import Vue from 'vue'
import $ from '../../core/KJQuery'
import _ from 'lodash'
import getScrollbarWidth from '../../core/utils/KScrollbarWidth'

const scrollbarWidth = getScrollbarWidth()

export const lockClass = 'km-modal__wrapper--locked'

class ModalMeta {
  id
  zIndex

  constructor({ id, zIndex }) {
    this.id = id
    this.zIndex = zIndex
  }
}

class ModalManager {
  instances = {} // 保存模态框（Modal）实例的容器
  modalStack = [] // 保存激活的模态框的基本信息（ModalMeta），用于控制层级以及背景的显示隐藏

  $backdrop // 遮罩，所有的 modal 共用一个背景

  backdropClass = 'km-modal__backdrop--active' // 遮罩的class
  delayHideBackdropClass = 'km-modal__backdrop--delay-hide'

  constructor() {
    this.$backdrop = $('<div>')
      .addClass('km-modal__backdrop')
      .on('touchmove', (e) => {
        e.preventDefault()
        e.stopPropagation()
      })
      .on('click', () => {
        this.onBackdropClick()
      })

    if (!Vue.prototype.$isServer) {
      $(document).on('keyup.modal.kamala', (e) => {
        if (e.keyCode !== 27) {
          return
        }

        const topModal = this.getTopModal()
        const instance = topModal && this.instances[topModal.id]
        if (instance && instance.keyboard) {
          instance.handleClose()
        }
      })
    }
  }

  register(instance) {
    if (instance && instance.id) {
      this.instances[instance.id] = instance
    }
  }

  deregister(id) {
    if (id) {
      this.instances[id] = null
      delete this.instances[id]
    }
  }

  onBackdropClick() {
    const topModal = this.getTopModal()
    if (!topModal) {
      return
    }

    const instance = this.instances[topModal.id]
    if (instance && instance.closeOnClickBackdrop) {
      instance.handleClose()
    }
  }

  getTopModal() {
    return _.last(this.modalStack)
  }

  /**
   * 给遮罩添加当前modal的位置class
   */
  addPlacementClass(placement) {
    const prefix = 'km-modal__backdrop--placement-'
    const classes = this.$backdrop[0].className.split(' ').filter(function (c) {
      return c.lastIndexOf(prefix, 0) !== 0
    })
    this.$backdrop[0].className = classes.join(' ').trim()
    const backdropPlacementClass = `${prefix}${placement}`
    this.$backdrop.addClass(backdropPlacementClass)
  }

  openModal({ id, zIndex, mask }) {
    const instance = this.instances[id]
    instance.$el.style.zIndex = zIndex

    this.addPlacementClass(instance.placement)

    if (!this.$backdrop.parent().length) {
      this.$backdrop.appendTo('body')
    }
    this.$backdrop[0].style.zIndex = zIndex

    this.lockOpener()

    // 是否展示遮罩
    // this.backdropClass = mask ? 'km-modal__backdrop--active' : 'km-modal__backdrop--transparent'
    const transparentBackdrop = 'km-modal__backdrop--transparent'
    const transparentModalWrapper = ' km-modal__wrapper--transparent'

    if (!this.modalStack.length) {
      this.$backdrop.addClass(this.backdropClass)

      // 是否展示遮罩,不需要时移除遮罩，防止透明的class一直存在
      if (!mask) {
        this.$backdrop.addClass(transparentBackdrop)
        instance.$el.className += transparentModalWrapper
      } else {
        this.$backdrop.removeClass(transparentBackdrop)
        instance.$el.className = instance.$el.className.replace(transparentModalWrapper, '')
      }
    }

    // 防止重复 open
    const exist = _.find(this.modalStack, ['id', id])
    if (!exist) {
      this.modalStack.push(
        new ModalMeta({
          id,
          zIndex,
        })
      )
    }
  }

  /**
   * 锁定打开新 Modal 的容器的滚动能力
   */
  lockOpener() {
    const topModal = this.getTopModal()
    const instance = topModal && this.instances[topModal.id]
    const $container = instance ? $(instance.$el) : $(document.body)

    const overflowYStyle = $container.css('overflowY')
    const hasOverflow = document.documentElement.clientHeight < $container[0].scrollHeight
    const containerPaddingRight = parseInt($container.css('paddingRight'))
    $container.data('paddingRight', containerPaddingRight)
    if (scrollbarWidth && (hasOverflow || overflowYStyle === 'scroll') && !$container.hasClass(lockClass)) {
      $container.css('paddingRight', scrollbarWidth + containerPaddingRight + 'px')
    }
    $container.addClass(lockClass)
  }

  unlockOpener() {
    const topModal = this.getTopModal()
    const instance = topModal && this.instances[topModal.id]
    const $container = instance ? $(instance.$el) : $(document.body)
    const paddingRight = $container.data('paddingRight')
    $container.css('paddingRight', paddingRight || '')
    $container.removeClass(lockClass)
  }

  closeModal(id) {
    let closedModalPlacement = 'center'
    if (!this.modalStack.length) {
      return
    }

    const modal = this.getTopModal()

    if (modal.id === id) {
      const instance = this.instances[id]
      closedModalPlacement = instance.placement
      this.modalStack.pop()
      const nextModal = this.getTopModal()
      if (nextModal) {
        this.$backdrop[0].style.zIndex = nextModal.zIndex
      }
    } else {
      _.remove(this.modalStack, ['id', id])
    }
    if (_.isEmpty(this.modalStack)) {
      const delayHide = closedModalPlacement !== 'center'
      if (delayHide) {
        this.$backdrop.addClass(this.delayHideBackdropClass)
      }
      this.$backdrop.removeClass(this.delayHideBackdropClass)
      this.$backdrop.remove()
      this.unlockOpener()
    } else {
      this.unlockOpener()
    }
  }
}

const manager = new ModalManager()

if (!Vue.prototype.$isServer) {
  window.KamalaModalManager = manager
}

export default manager
